import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  IconButton,
  Tooltip,
  LinearProgress,
  Alert,
} from '@mui/material';
import {
  Launch,
  Visibility,
  Warning,
  Error,
  CheckCircle,
  Info,
  Group,
  AttachMoney,
  Schedule,
  Gavel,
  Business,
  Assessment,
  Security,
} from '@mui/icons-material';
import { SearchResult } from '../store/slices/searchSlice';
import { fraudUtils } from '../services/api';

interface IrregularitySpecificCardsProps {
  contract: SearchResult;
  irregularityType: string;
  onViewDetails: () => void;
  onViewUSASpending: () => void;
  onDeepResearch?: () => void;
}

const IrregularitySpecificCards: React.FC<IrregularitySpecificCardsProps> = ({
  contract,
  irregularityType,
  onViewDetails,
  onViewUSASpending,
  onDeepResearch,
}) => {
  const getRiskIcon = (riskLevel: string) => {
    if (!riskLevel) {
      return <Info sx={{ color: '#9e9e9e' }} />;
    }
    switch (riskLevel.toUpperCase()) {
      case 'HIGH':
        return <Error sx={{ color: '#f44336' }} />;
      case 'MEDIUM':
        return <Warning sx={{ color: '#ff9800' }} />;
      case 'LOW':
        return <CheckCircle sx={{ color: '#4caf50' }} />;
      default:
        return <Info sx={{ color: '#9e9e9e' }} />;
    }
  };


  const getCardContent = () => {
    switch (irregularityType) {
      case 'sole-source':
      case 'low-competition':
        return <CompetitionIssuesCard contract={contract} irregularityType={irregularityType} />;
      
      case 'vendor-concentration':
        return <VendorIssuesCard contract={contract} />;
      
      case 'split-purchase':
      case 'excessive-pricing':
        return <PricingIssuesCard contract={contract} irregularityType={irregularityType} />;
      
      case 'timing-anomaly':
      case 'emergency-misuse':
        return <ComplianceIssuesCard contract={contract} irregularityType={irregularityType} />;
      
      default:
        return <DefaultCard contract={contract} />;
    }
  };

  return (
    <Card 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        bgcolor: 'white',
        border: '1px solid',
        borderColor: 'divider',
        position: 'relative',
        '&:hover': {
          boxShadow: 4,
          transform: 'translateY(-2px)',
          transition: 'all 0.2s ease-in-out',
        }
      }}
    >
      {/* Risk Level Indicator */}
      <Box sx={{ 
        position: 'absolute', 
        top: 10, 
        right: 10, 
        display: 'flex', 
        alignItems: 'center', 
        gap: 0.5 
      }}>
        {getRiskIcon(contract.fraud_risk_level)}
        <Chip
          label={contract.fraud_risk_level}
          size="small"
          color={
            contract.fraud_risk_level === 'CRITICAL' || contract.fraud_risk_level === 'HIGH' ? 'error' :
            contract.fraud_risk_level === 'MEDIUM' ? 'warning' : 'default'
          }
          variant="outlined"
        />
      </Box>

      <CardContent sx={{ flexGrow: 1, pt: 5 }}>
        {getCardContent()}
        
        {/* Competition Information */}
        {contract.solicitation_info && (
          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span style={{ fontSize: '16px' }}>📋</span>
              <strong>Solicitation Type:</strong>
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Chip
                label={contract.solicitation_info.code || 'Unknown'}
                size="small"
                color={
                  contract.solicitation_info.code === 'SSS' ? 'error' :
                  contract.solicitation_info.code === 'NP' ? 'warning' :
                  contract.solicitation_info.code === 'MAFO' ? 'info' : 'default'
                }
                sx={{ fontWeight: 'bold' }}
              />
              <Typography variant="caption" color="text.secondary">
                {contract.solicitation_info.description}
              </Typography>
            </Box>
            {contract.solicitation_info.competition_type && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Competition: {contract.solicitation_info.competition_type}
              </Typography>
            )}
          </Box>
        )}
        
        {/* Fraud Analysis Summary */}
        {contract.fraud_analysis && (
          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 1 }}>
            <Typography variant="body2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Assessment fontSize="small" />
              <strong>Fraud Analysis:</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {contract.fraud_analysis.recommendation}
            </Typography>
            {contract.fraud_analysis.flags && contract.fraud_analysis.flags.length > 0 && (
              <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {contract.fraud_analysis.flags.slice(0, 3).map((flag, index) => (
                  <Chip
                    key={index}
                    label={flag.replace(/_/g, ' ').toLowerCase()}
                    size="small"
                    variant="outlined"
                    color="warning"
                  />
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1, mt: 2, pt: 2, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="outlined"
            onClick={onViewDetails}
            startIcon={<Visibility />}
            sx={{ flex: '1 1 auto', minWidth: '100px' }}
          >
            View Details
          </Button>
          <Tooltip title="View on USAspending.gov">
            <IconButton
              size="small"
              onClick={onViewUSASpending}
              sx={{ border: 1, borderColor: 'divider' }}
            >
              <Launch fontSize="small" />
            </IconButton>
          </Tooltip>
          {onDeepResearch && (
            <Tooltip title="Start Deep Research Analysis">
              <Button
                size="small"
                variant="contained"
                onClick={onDeepResearch}
                startIcon={<Security fontSize="small" />}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  },
                  flex: '1 1 auto',
                  minWidth: '120px',
                }}
              >
                Deep Research
              </Button>
            </Tooltip>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

// Competition Issues Card (sole-source, low-competition)
const CompetitionIssuesCard: React.FC<{ contract: SearchResult; irregularityType: string }> = ({ contract, irregularityType }) => (
  <>
    <Typography variant="h6" gutterBottom noWrap sx={{ pr: 8 }}>
      {contract.title || contract.description || 'Contract Award'}
    </Typography>
    
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        <Business fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
        <strong>Vendor:</strong> {contract.vendor_name || 'N/A'}
      </Typography>
      
      {irregularityType === 'low-competition' && contract.number_of_offers_received && (
        <Typography variant="body2" color="text.secondary" gutterBottom>
          <Group fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
          <strong>Bidders:</strong> {contract.number_of_offers_received} offers received
        </Typography>
      )}
      
      <Typography variant="body2" color="text.secondary" gutterBottom>
        <AttachMoney fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
        <strong>Amount:</strong> {fraudUtils.formatCurrency(contract.total_obligated_amount || contract.amount || 0)}
      </Typography>
      
      <Typography variant="body2" color="text.secondary">
        <strong>Agency:</strong> {contract.agency || 'N/A'}
      </Typography>
    </Box>

    {irregularityType === 'low-competition' && contract.number_of_offers_received === 2 && (
      <Tooltip title="This contract received only 2 bids, indicating potential collusion or restricted competition">
        <Alert severity="warning" sx={{ mt: 1, cursor: 'help' }}>
          <Typography variant="caption">
            ⚠️ Only 2 bidders - High collusion risk
          </Typography>
        </Alert>
      </Tooltip>
    )}
  </>
);

// Vendor Issues Card (vendor-concentration)
const VendorIssuesCard: React.FC<{ contract: SearchResult }> = ({ contract }) => (
  <>
    <Typography variant="h6" gutterBottom noWrap sx={{ pr: 8 }}>
      {contract.vendor_name || 'Unknown Vendor'}
    </Typography>
    
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        <Gavel fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
        <strong>Contract:</strong> {contract.description || contract.title || 'N/A'}
      </Typography>
      
      <Typography variant="body2" color="text.secondary" gutterBottom>
        <AttachMoney fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
        <strong>Value:</strong> {fraudUtils.formatCurrency(contract.total_obligated_amount || contract.amount || 0)}
      </Typography>
      
      <Typography variant="body2" color="text.secondary" gutterBottom>
        <strong>Agency:</strong> {contract.agency || 'N/A'}
      </Typography>
      
      <Typography variant="body2" color="text.secondary">
        <Schedule fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
        <strong>Date:</strong> {fraudUtils.formatDate(contract.date)}
      </Typography>
    </Box>

    {contract.irregularity_indicators?.repeat_winner && (
      <Alert severity="warning" sx={{ mt: 1 }}>
        <Typography variant="caption">
          Vendor repeatedly wins similar contracts
        </Typography>
      </Alert>
    )}
  </>
);

// Pricing Issues Card (split-purchase/threshold-analysis, excessive-pricing)
const PricingIssuesCard: React.FC<{ contract: SearchResult; irregularityType: string }> = ({ contract, irregularityType }) => (
  <>
    <Typography variant="h6" gutterBottom sx={{ pr: 8 }}>
      {contract.title || contract.description || 'Purchase Order'}
      <Typography variant="h6" component="span" color="primary" sx={{ ml: 1 }}>
        {fraudUtils.formatCurrency(contract.total_obligated_amount || contract.amount || 0)}
      </Typography>
    </Typography>
    
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        <Business fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
        <strong>Vendor:</strong> {contract.vendor_name || 'N/A'}
      </Typography>
      
      {irregularityType === 'excessive-pricing' && contract.fraud_analysis?.net_score && (
        <Box sx={{ mt: 1, mb: 1 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>Price Anomaly Score:</strong>
          </Typography>
          <LinearProgress
            variant="determinate"
            value={Math.min(100, contract.fraud_analysis.net_score)}
            color={contract.fraud_analysis.net_score > 60 ? 'error' : 'warning'}
            sx={{ height: 6, borderRadius: 3 }}
          />
          <Typography variant="caption" color="text.secondary">
            {contract.fraud_analysis.net_score}% above market average
          </Typography>
        </Box>
      )}
      
      <Typography variant="body2" color="text.secondary" gutterBottom>
        <strong>Agency:</strong> {contract.agency || 'N/A'}
      </Typography>
      
      <Typography variant="body2" color="text.secondary">
        <Schedule fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
        <strong>Date:</strong> {fraudUtils.formatDate(contract.date)}
      </Typography>
    </Box>

    {contract.irregularity_indicators?.high_value_limited && (
      <Alert severity="error" sx={{ mt: 1 }}>
        <Typography variant="caption">
          High-value contract with pricing concerns
        </Typography>
      </Alert>
    )}
  </>
);

// Compliance Issues Card (timing-anomaly, emergency-misuse)
const ComplianceIssuesCard: React.FC<{ contract: SearchResult; irregularityType: string }> = ({ contract, irregularityType }) => {
  const timingAnalysis = (contract as any).timing_analysis || {};
  const emergencyAnalysis = (contract as any).emergency_analysis || {};
  
  // Debug logging
  console.log('ComplianceIssuesCard rendering for:', irregularityType);
  console.log('Emergency Analysis:', emergencyAnalysis);
  console.log('Contract data:', contract);
  
  return (
    <>
      <Typography variant="h6" gutterBottom sx={{ pr: 8 }}>
        {irregularityType === 'timing-anomaly' ? 'Timing Anomaly Detected' : 'Emergency Procurement Misuse'}
      </Typography>
      
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          <Gavel fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
          <strong>Contract:</strong> {(contract as any).prime_award_base_transaction_description || contract.description || contract.title || 'N/A'}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" gutterBottom>
          <Business fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
          <strong>Vendor:</strong> {
            (contract as any).vendor || 
            (contract as any).recipient_name || 
            contract.vendor_name || 
            (contract as any).awardee_or_recipient_legal_business_name ||
            (contract as any).vendor?.vendor_name ||
            'N/A'
          }
        </Typography>
        
        <Typography variant="body2" color="text.secondary" gutterBottom>
          <AttachMoney fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
          <strong>Amount:</strong> {fraudUtils.formatCurrency(
            (contract as any).current_total_value_of_award || 
            contract.total_obligated_amount || 
            contract.amount || 0
          )}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" gutterBottom>
          <Schedule fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
          <strong>Award Date:</strong> {fraudUtils.formatDate(
            (contract as any).award_base_action_date || contract.date
          )}
        </Typography>
        
        <Typography variant="body2" color="text.secondary">
          <strong>Agency:</strong> {(contract as any).awarding_agency_name || contract.agency || 'N/A'}
        </Typography>
      </Box>
      
      {/* Timing Analysis Details */}
      {irregularityType === 'timing-anomaly' && timingAnalysis.anomalies && timingAnalysis.anomalies.length > 0 && (
        <Box sx={{ mt: 1, mb: 1 }}>
          <Typography variant="caption" color="error" sx={{ fontWeight: 'bold' }}>
            Timing Issues:
          </Typography>
          <Box sx={{ ml: 1 }}>
            {timingAnalysis.anomalies.includes('WEEKEND_AWARD') && (
              <Chip label="Weekend Award" size="small" color="error" sx={{ mr: 0.5, mb: 0.5 }} />
            )}
            {timingAnalysis.anomalies.includes('HOLIDAY_AWARD') && (
              <Chip label="Holiday Award" size="small" color="error" sx={{ mr: 0.5, mb: 0.5 }} />
            )}
            {timingAnalysis.anomalies.includes('FISCAL_YEAR_END') && (
              <Chip label="Fiscal Year-End" size="small" color="warning" sx={{ mr: 0.5, mb: 0.5 }} />
            )}
            {timingAnalysis.anomalies.includes('MONTH_END_RUSH') && (
              <Chip label="Month-End Rush" size="small" color="warning" sx={{ mr: 0.5, mb: 0.5 }} />
            )}
            {timingAnalysis.anomalies.includes('RAPID_AWARD') && (
              <Chip label="Rapid Award" size="small" color="warning" sx={{ mr: 0.5, mb: 0.5 }} />
            )}
            {timingAnalysis.risk_score && (
              <Typography variant="caption" color="text.secondary" display="block">
                Risk Score: {timingAnalysis.risk_score}/100
              </Typography>
            )}
          </Box>
        </Box>
      )}
      
      {/* Emergency Analysis Details */}
      {irregularityType === 'emergency-misuse' && (
        <>
          {/* Simplified Emergency Type Display */}
          {(emergencyAnalysis.emergency_type || contract.disaster_emergency_fund_codes) && (
            <Box sx={{ mt: 1, mb: 1 }}>
              <Chip 
                label={`🚨 ${emergencyAnalysis.emergency_type || 'Emergency Procurement'}`}
                color={
                  (emergencyAnalysis.emergency_type && emergencyAnalysis.emergency_type.includes('COVID')) ? 'error' :
                  (emergencyAnalysis.emergency_type && emergencyAnalysis.emergency_type.includes('Disaster')) ? 'warning' :
                  (emergencyAnalysis.emergency_type && emergencyAnalysis.emergency_type.includes('Wildfire')) ? 'warning' :
                  'info'
                }
                variant="outlined"
                size="small"
              />
            </Box>
          )}

          {/* Key Misuse Indicators - Show only most critical ones */}
          {emergencyAnalysis.misuse_indicators && emergencyAnalysis.misuse_indicators.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="error" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                ⚠️ Issues:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {emergencyAnalysis.misuse_indicators.includes('ROUTINE_ITEM_AS_EMERGENCY') && (
                  <Chip label="Routine Item" size="small" color="error" />
                )}
                {emergencyAnalysis.misuse_indicators.includes('NO_EMERGENCY_CONTEXT') && (
                  <Chip label="No Context" size="small" color="warning" />
                )}
                {emergencyAnalysis.misuse_indicators.length > 2 && (
                  <Chip label={`+${emergencyAnalysis.misuse_indicators.length - 2} more`} size="small" variant="outlined" />
                )}
              </Box>
            </Box>
          )}
          
          {/* Simplified Risk Display */}
          {emergencyAnalysis.risk_score && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Risk Score: {emergencyAnalysis.risk_score}/100
            </Typography>
          )}
        </>
      )}
      
      <Alert 
        severity={contract.fraud_risk_level === 'CRITICAL' || contract.fraud_risk_level === 'HIGH' ? 'error' : 'warning'} 
        sx={{ mt: 1 }}
      >
        <Typography variant="caption">
          {irregularityType === 'timing-anomaly' 
            ? timingAnalysis.explanation || 'Contract awarded at suspicious time requiring investigation'
            : emergencyAnalysis.explanation || 'Emergency procurement authority may be misused'
          }
        </Typography>
      </Alert>
      
      {/* Risk Factors if available */}
      {contract.fraud_risk_factors && contract.fraud_risk_factors.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            <strong>Risk Factors:</strong>
          </Typography>
          <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
            {contract.fraud_risk_factors.slice(0, 3).map((factor: string, idx: number) => (
              <li key={idx}>
                <Typography variant="caption" color="text.secondary">
                  {factor}
                </Typography>
              </li>
            ))}
          </ul>
        </Box>
      )}
    </>
  );
};

// Default Card for fallback
const DefaultCard: React.FC<{ contract: SearchResult }> = ({ contract }) => (
  <>
    <Typography variant="h6" gutterBottom noWrap sx={{ pr: 8 }}>
      {contract.title || contract.description || 'Government Contract'}
    </Typography>
    
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        <Business fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
        <strong>Vendor:</strong> {contract.vendor_name || 'N/A'}
      </Typography>
      
      <Typography variant="body2" color="text.secondary" gutterBottom>
        <AttachMoney fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
        <strong>Amount:</strong> {fraudUtils.formatCurrency(contract.total_obligated_amount || contract.amount || 0)}
      </Typography>
      
      <Typography variant="body2" color="text.secondary">
        <strong>Agency:</strong> {contract.agency || 'N/A'}
      </Typography>
    </Box>
  </>
);

export default IrregularitySpecificCards;